import { shouldIgnoreFile } from './fileUtils';
import { convertElectronFileContentToBlob } from './binaryUtils';
import type { ElectronAPI } from '../../types/electron';

/**
 * Read all files recursively from a FileSystemDirectoryHandle (web browsers)
 * @param dirHandle - The directory handle to read from
 * @param rootPath - The root path for building relative paths
 * @param files - Accumulator array for files (used in recursion)
 * @returns Promise resolving to an array of File objects with webkitRelativePath
 */
export async function readFilesFromDirectoryHandle(
  dirHandle: FileSystemDirectoryHandle,
  rootPath: string = '',
  files: File[] = []
): Promise<File[]> {
  for await (const [name, handle] of dirHandle.entries()) {
    const relativePath = rootPath ? `${rootPath}/${name}` : name;

    // Skip ignored files
    if (shouldIgnoreFile(relativePath)) {
      continue;
    }

    if (handle.kind === 'file') {
      const fileHandle = handle as FileSystemFileHandle;
      const file = await fileHandle.getFile();
      // Create a File object with webkitRelativePath
      const fileWithPath = new File([file], name, {
        type: file.type,
        lastModified: file.lastModified,
      });
      // Add webkitRelativePath property
      Object.defineProperty(fileWithPath, 'webkitRelativePath', {
        value: relativePath,
        writable: false,
      });
      files.push(fileWithPath);
    } else if (handle.kind === 'directory') {
      const subDirHandle = handle as FileSystemDirectoryHandle;
      await readFilesFromDirectoryHandle(subDirHandle, relativePath, files);
    }
  }
  return files;
}

/**
 * File with Electron-specific properties for lazy loading
 */
interface FileWithElectronPath extends File {
  _electronFilePath?: string;
}

/**
 * Read all files recursively from a directory path using Electron API
 * @param electronAPI - The Electron API instance
 * @param basePath - The base directory path
 * @param relativePath - The relative path within the base (used in recursion)
 * @param files - Accumulator array for files (used in recursion)
 * @param readContent - Whether to read file content (default: true). If false, only reads metadata for faster performance.
 * @param onFileScanned - Optional callback for each file scanned
 * @returns Promise resolving to an array of File objects with webkitRelativePath
 */
export async function readFilesFromDirectoryElectron(
  electronAPI: ElectronAPI,
  basePath: string,
  relativePath: string = '',
  files: File[] = [],
  readContent: boolean = true,
  onFileScanned?: (filePath: string) => void
): Promise<File[]> {
  const fullPath = relativePath ? `${basePath}/${relativePath}` : basePath;

  try {
    const result = await electronAPI.readDirectory(fullPath);
    if (!result.success || !result.items) {
      return files;
    }

    for (const item of result.items) {
      const itemPath = relativePath
        ? `${relativePath}/${item.name}`
        : item.name;
      const itemFullPath = `${basePath}/${itemPath}`;

      // Skip ignored files
      if (shouldIgnoreFile(itemPath)) {
        continue;
      }

      if (item.isFile) {
        if (onFileScanned) {
          onFileScanned(itemPath);
        }
        try {
          if (readContent) {
            // Original behavior: read full file content
            const fileResult = await electronAPI.readFile(itemFullPath);
            if (fileResult.success && fileResult.content !== undefined) {
              const fileInfo = await electronAPI.getFileInfo(itemFullPath);

              const blob = convertElectronFileContentToBlob(fileResult)!;
              const file = new File([blob], item.name, {
                lastModified:
                  fileInfo.success && fileInfo.info
                    ? new Date(fileInfo.info.modified).getTime()
                    : Date.now(),
              });

              // Add webkitRelativePath property
              Object.defineProperty(file, 'webkitRelativePath', {
                value: itemPath,
                writable: false,
              });

              files.push(file);
            }
          } else {
            // Metadata-only mode: only read file info, create File with empty blob
            const fileInfo = await electronAPI.getFileInfo(itemFullPath);
            if (fileInfo.success && fileInfo.info) {
              // Create File object with empty blob but correct metadata
              const file = new File([new Blob()], item.name, {
                lastModified: new Date(fileInfo.info.modified).getTime(),
              });

              // Add webkitRelativePath property
              Object.defineProperty(file, 'webkitRelativePath', {
                value: itemPath,
                writable: false,
              });

              // Store the file path for later content reading
              Object.defineProperty(file, '_electronFilePath', {
                value: itemFullPath,
                writable: false,
              });

              // Override size property to return the actual file size
              Object.defineProperty(file, 'size', {
                value: fileInfo.info.size,
                writable: false,
                configurable: true,
              });

              files.push(file);
            }
          }
        } catch (error) {
          console.error(`Error reading file ${itemPath}:`, error);
        }
      } else if (item.isDirectory) {
        await readFilesFromDirectoryElectron(
          electronAPI,
          basePath,
          itemPath,
          files,
          readContent,
          onFileScanned
        );
      }
    }
  } catch (error) {
    console.error(`Error listing directory ${fullPath}:`, error);
  }

  return files;
}

/**
 * Read file content on-demand for upload (for files that were loaded in metadata-only mode)
 * @param electronAPI - The Electron API instance
 * @param filePath - The full path to the file
 * @param file - The existing File object (with empty blob) to update
 * @returns Promise resolving to a File object with actual content
 */
export async function readFileContentForUpload(
  electronAPI: ElectronAPI,
  filePath: string,
  file: File
): Promise<File> {
  try {
    const fileResult = await electronAPI.readFile(filePath);
    if (!fileResult.success || fileResult.content === undefined) {
      throw new Error(`Failed to read file content: ${filePath}`);
    }

    const blob = convertElectronFileContentToBlob(fileResult)!;

    // Create a new File object with the actual content, preserving all properties
    const fileWithContent = new File([blob], file.name, {
      lastModified: file.lastModified,
      type: file.type,
    });

    // Copy webkitRelativePath if it exists
    const fileWithPath = file as FileWithElectronPath;
    if (fileWithPath.webkitRelativePath) {
      Object.defineProperty(fileWithContent, 'webkitRelativePath', {
        value: fileWithPath.webkitRelativePath,
        writable: false,
      });
    }

    return fileWithContent;
  } catch (error) {
    console.error(`Error reading file content for upload ${filePath}:`, error);
    throw error;
  }
}

/**
 * Create a FileList-like object from an array of Files
 * @param files - Array of File objects
 * @returns A FileList containing the files
 */
export function createFileListFromArray(files: File[]): FileList {
  const dataTransfer = new DataTransfer();
  files.forEach((file) => dataTransfer.items.add(file));
  return dataTransfer.files;
}

/**
 * Convert Electron file result to a File object with webkitRelativePath
 * @param electronAPI - The Electron API instance
 * @param filePath - The full path to the file
 * @param relativePath - The relative path for webkitRelativePath
 * @param fileName - The file name
 * @returns Promise resolving to a File object or null if reading fails
 */
export async function convertElectronFileToFile(
  electronAPI: ElectronAPI,
  filePath: string,
  relativePath: string,
  fileName: string
): Promise<File | null> {
  try {
    const fileResult = await electronAPI.readFile(filePath);
    if (!fileResult.success || fileResult.content === undefined) {
      return null;
    }

    const fileInfo = await electronAPI.getFileInfo(filePath);
    const blob = convertElectronFileContentToBlob(fileResult)!;

    const file = new File([blob], fileName, {
      lastModified:
        fileInfo.success && fileInfo.info
          ? new Date(fileInfo.info.modified).getTime()
          : Date.now(),
    });

    // Add webkitRelativePath property
    Object.defineProperty(file, 'webkitRelativePath', {
      value: relativePath,
      writable: false,
    });

    return file;
  } catch (error) {
    console.error(`Error converting Electron file ${filePath}:`, error);
    return null;
  }
}

/**
 * Get file from path (for sync operations - Electron only)
 * @param filePath - The relative file path within the project
 * @param projectPath - The project base path
 * @param electronAPI - The Electron API instance
 * @param throwOnError - Preserve native read errors for transfer diagnostics instead of returning null
 * @returns Promise resolving to a File object or null if reading fails
 */
export async function getFileFromPath(
  filePath: string,
  projectPath: string,
  electronAPI: ElectronAPI,
  throwOnError = false
): Promise<File | null> {
  try {
    if (!projectPath) {
      return null;
    }

    const fullPath = `${projectPath}/${filePath}`;
    const fileResult = await electronAPI.readFile(fullPath);
    if (fileResult.success && fileResult.content !== undefined) {
      const fileInfo = await electronAPI.getFileInfo(fullPath);
      const fileName = filePath.split('/').pop() || 'file';

      const blob = convertElectronFileContentToBlob(fileResult)!;

      const file = new File([blob], fileName, {
        lastModified:
          fileInfo.success && fileInfo.info
            ? new Date(fileInfo.info.modified).getTime()
            : Date.now(),
      });

      return file;
    }
    throw new Error(fileResult.error || 'Native file read returned no content');
  } catch (error) {
    if (throwOnError) throw error;
    console.error(`Error accessing file ${filePath}:`, error);
    return null;
  }
}
